import { task } from 'mobx-task'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import type { WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { action, computed, observable, runInAction } from 'mobx'
import type { WorkspaceMembership } from '@/features/iam/workspaces/WorkspaceMembership'
import type Session from '@/features/iam/auth/Session'

/**
 * Allows the user to select a workspace if one was not preselected.
 */
export class WorkspaceSelectorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * List of workspaces that the user is a member of.
   */
  @observable.shallow accessor workspaces: WorkspaceMembership[] = []

  /**
   * Search text for searching workspaces.
   */
  @observable accessor searchText = ''

  constructor(
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _session: Session,
  ) {}

  @computed
  get filteredWorkspaces() {
    if (!this.searchText) {
      return this.workspaces
    }
    const text = this.searchText.toLowerCase()
    return this.workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(text) ||
        w.slug.toLowerCase().includes(text),
    )
  }

  activate = task(async () => {
    const { signal } = this._lifecycle.activate()
    const memberships =
      await this._workspaceStore.listWorkspaceMemberships(signal)

    signal.throwIfAborted()
    runInAction(() => (this.workspaces = memberships))
  })

  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  setSearchText(value: string) {
    this.searchText = value
  }

  selectWorkspace = task.resolved(async (membership: WorkspaceMembership) => {
    const workspace = await this._workspaceStore.fetchWorkspace(membership.id)
    if (!workspace) {
      return
    }

    this._lifecycle.signal.throwIfAborted()

    await this._session.selectWorkspace(workspace, this._lifecycle.signal)
  })
}
